/**
 * Client-side Encryption Utility
 * Encrypts/decrypts payload data to hide sensitive information in network tab
 */
import CryptoJS from 'crypto-js';

// Encryption secret key - should match server-side key
// In production, this should be dynamically loaded or generated per session
const ENCRYPTION_KEY = process.env.VUE_APP_ENCRYPTION_KEY || 'your-32-char-secret-key-here!!';

// Enable/disable encryption via environment variable
const ENCRYPTION_ENABLED = process.env.VUE_APP_PAYLOAD_ENCRYPTION === 'true';

/**
 * Encrypts data using AES-256
 * @param {Object|String} data - Data to encrypt
 * @returns {String} - Encrypted string
 */
export const encryptPayload = (data) => {
  if (!ENCRYPTION_ENABLED) {
    return data;
  }

  try {
    const jsonString = typeof data === 'string' ? data : JSON.stringify(data);
    const encrypted = CryptoJS.AES.encrypt(jsonString, ENCRYPTION_KEY).toString();

    return encrypted;
  } catch (error) {
    console.error('Encryption error:', error);
    return data; // Fallback to unencrypted data
  }
};

/**
 * Decrypts AES-256 encrypted data
 * @param {String} encryptedData - Encrypted string
 * @returns {Object|String} - Decrypted data
 */
export const decryptPayload = (encryptedData) => {
  if (!ENCRYPTION_ENABLED) {
    return encryptedData;
  }

  try {
    if (typeof encryptedData !== 'string') {
      return encryptedData; // Already decrypted or not encrypted
    }

    const decrypted = CryptoJS.AES.decrypt(encryptedData, ENCRYPTION_KEY);
    const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);

    if (!decryptedString) {
      console.warn('Decryption resulted in empty string, returning original data');
      return encryptedData;
    }

    // Try to parse as JSON, if fails return as string
    try {
      return JSON.parse(decryptedString);
    } catch {
      return decryptedString;
    }
  } catch (error) {
    console.error('Decryption error:', error);
    return encryptedData; // Fallback to original data
  }
};

/**
 * Checks if payload encryption is enabled
 * @returns {Boolean}
 */
export const isEncryptionEnabled = () => ENCRYPTION_ENABLED;

/**
 * Generates a hash of sensitive data for logging (without revealing actual data)
 * @param {String} data - Data to hash
 * @returns {String} - SHA256 hash
 */
export const hashForLogging = (data) => {
  return CryptoJS.SHA256(data).toString().substring(0, 16);
};
